import styles from './ReportPreviewMask.module.scss';

import { gql, useQuery } from '@apollo/client';
import { Download, Fullscreen, Settings } from '@mui/icons-material';
import PrintIcon from '@mui/icons-material/Print';
import {
  Box,
  IconButton,
  LinearProgress,
  Popover,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import printJS from 'print-js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BanderoleInfo } from '@work4all/components/lib/components/banderole/BanderoleInfo';
import { ReportType } from '@work4all/components/lib/components/entity-picker/report-type-picker/ReportTypePicker';
import { TextmarkContext } from '@work4all/components/lib/components/entity-picker/textmark-picker';
import { useErpFileName } from '@work4all/components/lib/components/entity-preview/erp-preview/hooks/use-erp-file-name';
import { Tooltip } from '@work4all/components/lib/components/tooltip/Tooltip';
import { IconButtonWithTooltip } from '@work4all/components/lib/input/actions/IconButtonWithTooltip';
import { useHistoryStack } from '@work4all/components/lib/navigation/history-stack';
import {
  FilePreview,
  FilePreviewProvider,
  useFilePreview,
} from '@work4all/components/lib/preview/FilePreviewProvider';
import { MIME_TYPES } from '@work4all/components/lib/preview/Preview';

import { useConvertToTempFile, useDataProvider } from '@work4all/data';
import { useAuthHeaders } from '@work4all/data/lib/auth/use-auth-headers';
import { useEntityEvents } from '@work4all/data/lib/entity-events/use-entity-events';
import { useSetArchivePdf } from '@work4all/data/lib/hooks/use-set-archive-pdf';
import { useUploadTempFile } from '@work4all/data/lib/hooks/use-upload-temp-file';
import { usePopoverState } from '@work4all/data/lib/hooks/usePopoverState';
import { useSetting } from '@work4all/data/lib/settings';
import { downloadAuthed, getObjectUrlAuthed } from '@work4all/data/lib/utils';

import { IAttachmentEntity } from '@work4all/models';
import { CreateCrystalReportRequest } from '@work4all/models/lib/Classes/CreateCrystalReportRequest.entity';
import { Customer } from '@work4all/models/lib/Classes/Customer.entity';
import { ERPTypes } from '@work4all/models/lib/Classes/ERPTypes.entity';
import { ExecuteReport2Request } from '@work4all/models/lib/Classes/ExecuteReport2Request.entity';
import { FileEntity } from '@work4all/models/lib/Classes/FileEntity.entity';
import { Invoice } from '@work4all/models/lib/Classes/Invoice.entity';
import { Report } from '@work4all/models/lib/Classes/Report.entity';
import { Report2 } from '@work4all/models/lib/Classes/Report2.entity';
import { ReportOptionsValue } from '@work4all/models/lib/Classes/ReportOptionsValue.entity';
import { TempFile } from '@work4all/models/lib/Classes/TempFile.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { BzObjType } from '@work4all/models/lib/Enums/BzObjType.enum';
import { ConvertInvoiceToRaStatuses } from '@work4all/models/lib/Enums/ConvertInvoiceToRaStatuses.enum';
import { EInvoiceFormat } from '@work4all/models/lib/Enums/EInvoiceFormat.enum';
import { EMailTemplateKind } from '@work4all/models/lib/Enums/EMailTemplateKind.enum';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { InvoiceForm } from '@work4all/models/lib/Enums/InvoiceForm.enum';
import { ReportBzObjType } from '@work4all/models/lib/Enums/ReportBzObjType.enum';

import { NavigationOverlayHeader } from '../../../../../../../../components/navigation-overlay-header/NavigationOverlayHeader';
import { useLanguageManager } from '../../../../../../../../providers/LanguageManagerProvider';
import { settings } from '../../../../../../../../settings';
import {
  ApiErrors,
  ValidationErrors,
} from '../../../../../../../apollo/ValidationErrors';
import { EmailTemplateButtonProvider } from '../../../../../../components/email-template-button/EmailTemplateButtonProvider';
import { EmailTemplateIconButton } from '../../../../../../components/email-template-button/EmailTemplateIconButton';
import { SimplePDFReport } from '../../simple-pdf-report/SimplePDFReport';
import { bzObjToEntityType } from '../../simple-pdf-report/utils';

import { ReportPreviewConfPanel } from './components/report-preview-left-panel/ReportPreviewConfPanel';
import {
  ConvertInvoiceToRaProvider,
  useConvertInvoiceToRaContext,
} from './convert-invoice-to-ra';
import { useArticleAttachments } from './use-article-attachments';
import { useAttachZugferdToTempfile } from './use-attach-zugferd-to-tempfile';
import { useInvoiceToXInvoice } from './use-invoice-to-x-invoice';
import { useReportValues } from './use-report-values';

interface ReportPreviewMaskProps extends BaseReportPreviewMaskProps {
  id: number | string;
}

interface CrystalReportResponse {
  createCrystalReport: CreateCrystalReport;
}

interface CreateCrystalReport {
  displayName: string;
  tempFile: TempFile;
  tempFileID: string;
  typename: string;
}

const CRYSTAL_REPORT_QUERY = gql`
  query CreateCrystalReport($input: CreateCrystalReportRequest) {
    createCrystalReport(input: $input) {
      displayName
      tempFile {
        dateiname
        fileInfos {
          downloadMimeType
          downloadUrl
          fileEntityFilename
          previewUrl
          previewMimeType
          fileSize
          fileServiceProviderInfos {
            customUrl
            exists
            fspUrl
            thumbnail
            size
            mimeType
            key
            id
            filename
          }
          fileRequestType
        }
        id
        datum
      }
      tempFileId
    }
  }
`;

export const ReportPreviewMask = (props: ReportPreviewMaskProps) => {
  const { id, reportType: incomingReportType, bzObjectType } = props;
  const [report, setReport] = useState<Report | Report2 | null>(props.report);

  const isOnPremise = useSetting(settings.isOnPremiseEnvironment());

  const { values: reportOptionValues, onChange: onChangeReportOptionValues } =
    useReportValues({
      reportId: report?.id,
      bzObjectType,
    });
  const [reportType, setReportType] = useState<ReportType>(ReportType.Simple);

  useEffect(() => {
    setReportType(incomingReportType);
  }, [incomingReportType]);

  const { loading, data, error } = useQuery<
    CrystalReportResponse,
    { input: CreateCrystalReportRequest }
  >(CRYSTAL_REPORT_QUERY, {
    skip:
      !report || reportType !== ReportType.CrystalReports || isOnPremise.value,
    fetchPolicy: 'no-cache',
    variables: {
      input: {
        bzObjType:
          bzObjectType === BzObjType.ANGEBOT
            ? ReportBzObjType.ANGEBOT
            : bzObjectType === BzObjType.AUFTRAG
            ? ReportBzObjType.AUFTRAG
            : (report as Report)?.bzObjType,
        reportCode: (report as Report)?.id,
        objectCode: parseInt(id.toString()),
        options: Object.keys(reportOptionValues).map((key) => ({
          optionId: parseInt(key),
          value: reportOptionValues[key],
        })),
      },
    },
  });
  const parsedErrors = error
    ? ValidationErrors.parseErrors(error?.graphQLErrors)
    : undefined;

  const { set: setLastUsedReportType } = useSetting(
    settings.lastUsedReportType()
  );

  const handleReportTypeSelection = (value: ReportType) => {
    setLastUsedReportType(value);
    setReportType(value);
  };

  return (
    <FilePreviewProvider file={data?.createCrystalReport.tempFile}>
      <ConvertInvoiceToRaProvider invoiceId={Number(id)} reportId={report?.id}>
        <ReportPreviewMaskInternal
          {...props}
          isOnPremise={isOnPremise.value}
          bzObjectId={id}
          errors={parsedErrors}
          report={report}
          loading={loading}
          setReport={setReport}
          reportType={reportType}
          onReportTypeChange={handleReportTypeSelection}
          reportOptionValues={reportOptionValues}
          onChangeReportOptionValues={onChangeReportOptionValues}
        />
      </ConvertInvoiceToRaProvider>
    </FilePreviewProvider>
  );
};

interface ReportPreviewMaskInternalProps extends BaseReportPreviewMaskProps {
  setReport: (report: Report | Report2) => void;
  errors?: ApiErrors;
  loading: boolean;
  onReportTypeChange: (value: ReportType) => void;
  reportType: ReportType;
  bzObjectId?: string | number;
  isOnPremise: boolean;
}

interface BaseReportPreviewMaskProps {
  contactId?: number;
  businessPartnerId?: number;
  businessPartnerType?: Entities.customer | Entities.supplier;
  businessPartnerLanguageId?: number;
  report: Report | Report2 | null;
  reportType: ReportType;
  data?: ERPTypes;
  reportBzObjectType?: ReportBzObjType;
  bzObjectType?: BzObjType;
  entity: Entities;
  reportOptionValues?: Record<number, boolean>;
  onChangeReportOptionValues?: (optId: number, value: boolean) => void;
}

const ReportPreviewMaskInternal = (props: ReportPreviewMaskInternalProps) => {
  const {
    bzObjectType,
    reportBzObjectType,
    report,
    setReport,
    contactId,
    businessPartnerId,
    businessPartnerType,
    businessPartnerLanguageId,
    errors,
    loading,
    data,
    bzObjectId,
    reportType,
    onReportTypeChange,
    reportOptionValues,
    isOnPremise,
    onChangeReportOptionValues,
  } = props;
  const { t } = useTranslation();
  const { file: crystalreportsFile, openFullscreen } = useFilePreview();

  const [devExpressReportOptions, setDevExpressReportOptions] = useState<
    ReportOptionsValue[]
  >([]);

  const devExpressReportFileReq = useMemo<DataRequest>(() => {
    return {
      operationName: 'GetDevExpressReportFile',
      data: {
        id: null,
        fileName: null,
        fileInfos: {
          downloadMimeType: null,
          downloadUrl: null,
          fileEntityFilename: null,
          fileRequestType: null,
          fileServiceProviderInfos: {
            customUrl: null,
            exists: null,
            fileName: null,
            fspUrl: null,
            id: null,
            key: null,
            mimeType: null,
            size: null,
            thumbnail: null,
          },
          fileSize: null,
          previewMimeType: null,
          previewUrl: null,
        },
      } as TempFile<EMode.query>,
      entity: Entities.tempFile,
      completeDataResponse: true,
      skip: reportType !== ReportType.DevExpress,
      vars: {
        input: {
          entityKey: {
            objectCode: Number(bzObjectId),
            objectType: bzObjectType,
          } as ExecuteReport2Request,
          options: devExpressReportOptions?.length
            ? devExpressReportOptions
            : undefined,
          reportId: report?.id,
        },
      },
    };
  }, [
    bzObjectId,
    bzObjectType,
    devExpressReportOptions,
    report?.id,
    reportType,
  ]);

  const devExpressReportFileRes = useDataProvider<TempFile>(
    devExpressReportFileReq,
    !report
  );

  const devExpressReportFile = devExpressReportFileRes.data as TempFile;

  const xInvoiceFile = useMemo(
    () => ({
      id: 'x-invoice-file',
      fileName: '',
      fileInfos: {
        downloadUrl: (data as Invoice).xInvoicePreviewUrl,
        previewUrl: (data as Invoice).xInvoicePreviewUrl,
        downloadMimeType: MIME_TYPES.pdf,
        previewMimeType: MIME_TYPES.pdf,
        fileEntityFilename: '',
      } as FileEntity,
    }),
    [data]
  );

  const [simpleReportBlob, setSimpleReportBlob] = useState<Blob>(null);
  const httpHeaders = useAuthHeaders();

  const uploadTempFile = useUploadTempFile();
  const converToTempFile = useConvertToTempFile();
  const setArchivePdf = useSetArchivePdf();

  const [showSimpleReportConf, setShowSimpleReportConf] = useState(false);

  const isSmDown = useMediaQuery<Theme>((t) => t.breakpoints.down('sm'));

  const entityType = bzObjToEntityType[bzObjectType];

  const { resources: languageResources } = useLanguageManager();

  const articleImages = useArticleAttachments({ entityType, data });

  const initialFileName = useErpFileName({
    data,
    entityType,
    languageResources,
  });

  const isXInvoice = useMemo(() => {
    const customer: Customer = data.additionalAddress2?.businessPartner?.data;

    return (
      entityType === Entities.invoice &&
      customer?.invoiceForm === InvoiceForm.ELEKTRONISCH &&
      [EInvoiceFormat.X_RECHNUNG_CII, EInvoiceFormat.X_RECHNUNG_UBL].includes(
        customer?.eInvoiceFormat
      )
    );
  }, [data.additionalAddress2?.businessPartner?.data, entityType]);

  const resultingReportFile = useMemo(() => {
    if (isXInvoice) {
      return xInvoiceFile;
    }
    switch (reportType) {
      case ReportType.CrystalReports:
        return crystalreportsFile;
      case ReportType.DevExpress:
        return devExpressReportFile;
    }
    return null;
  }, [
    crystalreportsFile,
    devExpressReportFile,
    isXInvoice,
    reportType,
    xInvoiceFile,
  ]);

  const filePreviewProps = {
    initialScale: isSmDown ? undefined : 0.6,
    initalTranslate: isSmDown ? undefined : 'translate(30%, 50px)',
  };

  const [fileName, setFileName] = useState(initialFileName);

  useEffect(() => {
    setFileName(initialFileName);
  }, [initialFileName]);

  const { convertInvoice, eInvoiceType } = useInvoiceToXInvoice({
    data,
    entityType,
    id: bzObjectId,
  });

  const { attachZugferdToTempFile } = useAttachZugferdToTempfile();

  const getEmailParams = useCallback(async () => {
    const tempFileAttachements = [];
    let attachment: IAttachmentEntity = {
      date: new Date().toISOString(),
      fileInfos: resultingReportFile?.fileInfos,
      fileName,
      id: resultingReportFile?.id,
    };
    if (!isXInvoice) {
      if (reportType === ReportType.Simple) {
        try {
          const uploadFile = new File([simpleReportBlob], fileName);
          const {
            generatedObject,
            downloadUrl,
            previewMimeType,
            downloadUrlForPreview,
            downloadMimeType,
          } = await uploadTempFile(uploadFile);

          setArchivePdf({
            subObjectTargetType: bzObjectType,
            target: 'ArchivPdf',
            targetCode: parseInt(bzObjectId.toString()),
            tempfileId: generatedObject,
          });

          if (eInvoiceType === EInvoiceFormat.ZUG_FE_RD) {
            const file = await attachZugferdToTempFile({
              variables: {
                invoiceCode:
                  typeof bzObjectId === 'string'
                    ? parseInt(bzObjectId)
                    : bzObjectId,
                tempPdfFile: generatedObject,
              },
            });
            tempFileAttachements.push(file.data.attachZugferdToTempfile);
          } else {
            attachment = {
              date: new Date().toISOString(),
              fileInfos: {
                downloadMimeType,
                downloadUrl,
                previewMimeType,
                previewUrl: downloadUrlForPreview,
                fileEntityFilename: fileName,
                fileSize: uploadFile.size,
              },
              fileName,
              id: generatedObject,
            };
            tempFileAttachements.push(attachment);
          }
        } catch (err) {
          console.warn(err);
        }
      } else {
        tempFileAttachements.push(attachment);
      }
    }

    if (isXInvoice) {
      const result = await convertInvoice();
      tempFileAttachements.push(result.data.invoiceToXInvoice);
    }

    const entityTemplate = businessPartnerId
      ? {
          entity: contactId ? Entities.contact : businessPartnerType,
          ids: [
            contactId
              ? `${contactId}:${businessPartnerType}:${businessPartnerId}`
              : businessPartnerId,
          ],
        }
      : undefined;

    if (articleImages.length) {
      const articleImageTempFiles = await Promise.all(
        articleImages.map((articleImage) =>
          converToTempFile(
            articleImage.fileInfos.downloadUrl,
            articleImage.fileName
          )
        )
      );

      articleImageTempFiles.forEach(({ attachment }) => {
        tempFileAttachements.push(attachment);
      });
    }

    return {
      entityTemplate,
      params: {
        tempFileAttachements: JSON.stringify(tempFileAttachements),
        processedMailTemplateArgs: JSON.stringify({
          project: data.project,
          projectId: data.projectId,
          noDirectContact: !contactId,
          textFieldCustom: [
            {
              key: 'ERP' satisfies TextmarkContext,
              value: JSON.stringify({
                kind: t(`COMMON.${props.entity?.toUpperCase()}`),
                note: data?.note?.split('\n')[0],
                number:
                  props.entity === Entities.contract
                    ? data?.contractNumber
                    : data?.number,
              }),
            },
          ],
        }),
      },
    };
  }, [
    resultingReportFile?.fileInfos,
    resultingReportFile?.id,
    fileName,
    isXInvoice,
    businessPartnerId,
    contactId,
    businessPartnerType,
    articleImages,
    data.project,
    data.projectId,
    data?.note,
    data?.contractNumber,
    data?.number,
    t,
    props.entity,
    reportType,
    simpleReportBlob,
    uploadTempFile,
    setArchivePdf,
    bzObjectType,
    bzObjectId,
    eInvoiceType,
    attachZugferdToTempFile,
    convertInvoice,
    converToTempFile,
  ]);

  const download = useCallback(
    async (e) => {
      e.preventDefault();

      let downloadUrl = resultingReportFile?.fileInfos?.downloadUrl;

      if (reportType === ReportType.Simple) {
        try {
          const uploadFile = new File([simpleReportBlob], fileName);
          const { downloadUrl: tmpDownloadUrl } = await uploadTempFile(
            uploadFile
          );
          downloadUrl = tmpDownloadUrl;
        } catch (err) {
          console.warn(err);
        }
      }

      downloadAuthed(downloadUrl, fileName, httpHeaders);
    },
    [
      resultingReportFile?.fileInfos?.downloadUrl,
      reportType,
      fileName,
      httpHeaders,
      simpleReportBlob,
      uploadTempFile,
    ]
  );

  const { close } = useHistoryStack();
  const { isEnabled, convert, alert, reportId } =
    useConvertInvoiceToRaContext();

  const convertInvoiceToRaHandler = useCallback(() => {
    if (reportType === ReportType.Simple || !isEnabled || !reportId) return;

    convert().then((response) => {
      alert(response.invoiceToOutgoingInvoiceLedger);

      if (
        response.invoiceToOutgoingInvoiceLedger ===
        ConvertInvoiceToRaStatuses.SUCCESS
      ) {
        close();
      }
    });
  }, [alert, close, convert, isEnabled, reportId, reportType]);

  const print = useCallback(
    async (e) => {
      e.preventDefault();

      convertInvoiceToRaHandler();

      let downloadUrl = resultingReportFile?.fileInfos?.downloadUrl;

      if (reportType === ReportType.Simple) {
        try {
          const uploadFile = new File([simpleReportBlob], fileName);
          const { downloadUrl: tmpDownloadUrl } = await uploadTempFile(
            uploadFile
          );
          downloadUrl = tmpDownloadUrl;
        } catch (err) {
          console.warn(err);
        }
      }

      const object = await getObjectUrlAuthed(downloadUrl, httpHeaders);
      printJS(object);
    },
    [
      convertInvoiceToRaHandler,
      resultingReportFile?.fileInfos?.downloadUrl,
      reportType,
      httpHeaders,
      simpleReportBlob,
      fileName,
      uploadTempFile,
    ]
  );

  const isDesktop = useMediaQuery<Theme>((theme) => theme.breakpoints.up('md'));
  const confPopover = usePopoverState();

  const renderBreadcrumbsChildren = () => (
    <div className={styles['header-wrapper']}>
      <IconButton color="primary" onClick={print}>
        <PrintIcon />
      </IconButton>
      <IconButton color="primary" onClick={download}>
        <Download />
      </IconButton>

      <EmailTemplateIconButton />

      {isDesktop && reportType === ReportType.CrystalReports && (
        <IconButtonWithTooltip
          tooltip={t('COMMON.FULLSCREEN')}
          icon={<Fullscreen />}
          onClick={openFullscreen}
        />
      )}
      {!isDesktop && (
        <IconButton color="primary" onClick={confPopover.handleClick}>
          <Settings />
        </IconButton>
      )}
    </div>
  );

  useEntityEvents((event) => {
    if (
      event.entity === Entities.eMail &&
      event.type === 'upsert' &&
      event.data
    ) {
      convertInvoiceToRaHandler();
    }
  });

  const overrideRecipients =
    bzObjectType === BzObjType.RECHNUNG
      ? data.businessPartnerContactCombined?.businessPartner?.data
          ?.invoiceEMailAddress
      : undefined;
  const errorComponent = errors?.length ? (
    <>
      {errors.map((x) => (
        <Typography key={x.code} component="span" variant="body2" color="error">
          {ValidationErrors.translateError(x.code)}
        </Typography>
      ))}
    </>
  ) : null;

  const confPanel = (
    <ReportPreviewConfPanel
      reportType={reportType}
      onReportTypeChange={onReportTypeChange}
      report={report}
      reportBzObjectType={reportBzObjectType}
      setReport={setReport}
      fileName={fileName}
      setFileName={setFileName}
      reportId={reportId}
      entityType={entityType}
      bzObjectType={bzObjectType}
      reportOptionValues={reportOptionValues}
      onChangeReportOptionValues={onChangeReportOptionValues}
      data={data}
      showSimpleReportConf={showSimpleReportConf}
      setShowSimpleReportConf={setShowSimpleReportConf}
      errorComponent={errorComponent}
      onDevExpressReportOptionsChange={setDevExpressReportOptions}
    />
  );

  return (
    <EmailTemplateButtonProvider
      eMailTemplateKind={EMailTemplateKind.ERP_OBJECTS}
      getEmailParams={getEmailParams}
      noTemplate
      languageId={businessPartnerLanguageId}
      overrideRecipients={overrideRecipients}
    >
      <div className={styles['wrapper']}>
        <NavigationOverlayHeader
          title={t(isXInvoice ? 'COMMON.X_INVOICE_PREVIEW' : 'COMMON.PREVIEW')}
          breadcrumbsChildren={
            resultingReportFile || simpleReportBlob
              ? renderBreadcrumbsChildren()
              : undefined
          }
          forceStackItems
        />
        {(data.releaseInformation?.releaseNeeded ||
          (isOnPremise && reportType === ReportType.CrystalReports)) && (
          <BanderoleInfo
            visible
            text={
              isOnPremise && reportType === ReportType.CrystalReports ? (
                t('ALERTS.ON_PREMISE_REPORT')
              ) : (
                <Box display="flex" gap="0.5rem" padding="1rem 0 ">
                  {t('ALERTS.RELEASE_PROCESS', {
                    entity: t(`COMMON.${props.entity.toUpperCase()}_other`),
                  })}
                  <Tooltip title={t('ALERTS.RELEASE_PROCESS_TOOLTIP')}>
                    <Typography variant="body2" color="var(--brand01)">
                      {t('ALERTS.RELEASE_PROCESS_MORE')}
                    </Typography>
                  </Tooltip>
                </Box>
              )
            }
          />
        )}

        <Popover {...confPopover}>{confPanel}</Popover>

        <div className={styles['content-wrapper']}>
          {!isXInvoice && (
            <div className={styles['left-panel']}>{confPanel}</div>
          )}
          <div className={styles['right-content']}>
            {isXInvoice && (
              <FilePreview file={xInvoiceFile} {...filePreviewProps} />
            )}
            {!isXInvoice && (
              <>
                {((devExpressReportFileRes.loading &&
                  reportType === ReportType.DevExpress) ||
                  (loading && reportType === ReportType.CrystalReports)) && (
                  <LinearProgress />
                )}
                {!isDesktop && errors?.length && (
                  <div className={styles['mobile-error']}>{errorComponent}</div>
                )}
                {crystalreportsFile &&
                  reportType === ReportType.CrystalReports && (
                    <FilePreview
                      file={crystalreportsFile}
                      {...filePreviewProps}
                    />
                  )}
                {devExpressReportFile &&
                  reportType === ReportType.DevExpress && (
                    <FilePreview
                      file={devExpressReportFile}
                      {...filePreviewProps}
                    />
                  )}

                {reportType === ReportType.Simple && data && (
                  <SimplePDFReport
                    bzObjType={bzObjectType}
                    data={data}
                    onPdfBlobChange={setSimpleReportBlob}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </EmailTemplateButtonProvider>
  );
};
